/**
 * RECEIVE. Module 04 Payments (FR-U-TXN-002, FR-U-WAL-001).
 *
 * A person who cannot easily be paid does not really have a wallet. This screen turns the participant
 * id into something scannable and shareable, and lets them request a specific amount. The id is not a
 * secret: it is an account number, and the whole point is to hand it out.
 *
 * The QR sits on a white tile in BOTH themes, on purpose. A QR needs high contrast to scan, and a
 * "dark-mode" QR with light modules on a near-black card is a QR that phones struggle to read.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { BRAND_PINE, WHITE } from '@/lib/brand-colors';
import { copyText } from '@/lib/clipboard';
import { useSession } from '@/lib/session-context';
import { Sheet } from '@/components/overlay';

export default function Receive() {
  const { me } = useSession();
  const id = me?.participant_id ?? '';

  const [amount, setAmount] = useState('');
  const [amountOpen, setAmountOpen] = useState(false);
  const [copied, setCopied] = useState<'id' | 'link' | null>(null);
  const [copyFailed, setCopyFailed] = useState(false);

  // A simple payment-request URI. In production this becomes a universal link the app resolves; here
  // it encodes the recipient and, when set, the requested amount.
  const uri = useMemo(() => {
    const base = `vasp:pay?to=${id}`;
    return amount ? `${base}&amount=${encodeURIComponent(amount)}` : base;
  }, [id, amount]);

  async function copy(what: 'id' | 'link') {
    setCopyFailed(false);
    const ok = await copyText(what === 'id' ? id : uri);
    if (ok) {
      setCopied(what);
      setTimeout(() => setCopied(null), 1500);
    } else {
      // The clipboard was refused (an insecure origin, or a browser that blocks it). Say so, rather
      // than leaving a Copy button that looks like it worked.
      setCopyFailed(true);
      setTimeout(() => setCopyFailed(false), 3000);
    }
  }

  async function share() {
    const text = amount ? `Pay me KES ${amount} on VASP` : 'Pay me on VASP';
    if (navigator.share) {
      try {
        await navigator.share({ title: 'VASP', text, url: uri });
      } catch {
        /* user dismissed the share sheet */
      }
    } else {
      void copy('link');
    }
  }

  return (
    <div className="mx-auto max-w-md space-y-5">
      <div className="flex items-center gap-2">
        <Link to="/wallet" className="text-sm text-ink-muted hover:text-ink">
          &larr; Wallet
        </Link>
      </div>

      <header>
        <h1 className="text-page-title text-ink">Receive</h1>
        <p className="mt-0.5 text-sm text-ink-muted">
          Show this to whoever is paying you. They must be verified on VASP too.
        </p>
      </header>

      <section className="rounded-xl border border-border bg-surface p-6 shadow-card">
        {/* The QR, on a white tile in both themes for scannability. */}
        <div className="mx-auto w-fit rounded-xl bg-white p-4 shadow-soft">
          {id ? (
            <QRCodeSVG
              value={uri}
              size={200}
              fgColor={BRAND_PINE}
              bgColor={WHITE}
              level="M"
              marginSize={0}
            />
          ) : (
            <div className="h-[200px] w-[200px] animate-pulse rounded-lg bg-surface-sunken" />
          )}
        </div>

        {amount ? (
          <p className="mt-4 text-center">
            <span className="text-sm text-ink-muted">Requesting </span>
            <span className="tabular text-lg font-semibold text-ink">KES {amount}</span>
          </p>
        ) : (
          <p className="mt-4 text-center text-sm text-ink-muted">Any amount</p>
        )}

        <button
          onClick={() => setAmountOpen(true)}
          className="mx-auto mt-1 block text-sm font-medium text-brand hover:underline"
        >
          {amount ? 'Change amount' : 'Request a specific amount'}
        </button>

        {/* The id, for manual entry. */}
        <div className="mt-5 flex items-center gap-2 border-t border-border pt-4">
          <code className="min-w-0 flex-1 truncate rounded-field bg-surface-sunken px-3 py-2 font-mono text-xs text-ink">
            {id}
          </code>
          <button
            onClick={() => copy('id')}
            className="shrink-0 rounded-field border border-border px-3 py-2 text-sm font-medium text-ink transition hover:bg-surface-sunken"
          >
            {copied === 'id' ? 'Copied' : 'Copy'}
          </button>
        </div>
      </section>

      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={share}
          className="flex items-center justify-center gap-2 rounded-field bg-primary py-3 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-light"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.8}
              d="M8.7 10.7 15.3 7m0 10-6.6-3.7M18 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm0 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM8 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z"
            />
          </svg>
          Share
        </button>
        <button
          onClick={() => copy('link')}
          className="flex items-center justify-center gap-2 rounded-field border border-border bg-surface py-3 text-sm font-medium text-ink transition hover:bg-surface-sunken"
        >
          {copied === 'link' ? 'Link copied' : 'Copy link'}
        </button>
      </div>

      {copyFailed ? (
        <p role="alert" className="rounded-card bg-danger/5 p-3 text-center text-xs text-danger">
          We could not copy automatically. Press and hold the ID above to copy it by hand.
        </p>
      ) : null}

      <p className="rounded-card bg-surface-sunken p-3 text-center text-xs text-ink-muted">
        Anyone with your ID can send to you, but only verified participants can pay, and every
        payment runs the same compliance checks.
      </p>

      {/* The other way to be paid. A QR needs a screen in front of somebody; a Till can be written
          on a card, said down a phone, or printed on a receipt. */}
      <Link
        to="/till"
        className="flex items-center justify-between gap-3 rounded-card border border-border bg-surface p-3.5 text-sm shadow-card transition hover:bg-surface-sunken"
      >
        <span>
          <span className="font-medium text-ink">Being paid in crypto?</span>{' '}
          <span className="text-ink-muted">
            A Till is one short number that names its own asset and network.
          </span>
        </span>
        <span aria-hidden className="shrink-0 text-ink-subtle">
          &rarr;
        </span>
      </Link>

      {/* Amount sheet. */}
      <Sheet open={amountOpen} onClose={() => setAmountOpen(false)} title="Request an amount">
        <div className="flex items-center gap-2 rounded-field border border-border px-3 py-2.5 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/25">
          <span className="text-sm text-ink-muted">KES</span>
          <input
            autoFocus
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
            placeholder="0.00"
            className="w-full bg-transparent font-mono text-base text-ink outline-none"
          />
        </div>
        <div className="mt-4 flex gap-2">
          {amount ? (
            <button
              onClick={() => {
                setAmount('');
                setAmountOpen(false);
              }}
              className="flex-1 rounded-field border border-border py-2.5 text-sm font-medium text-ink transition hover:bg-surface-sunken"
            >
              Clear
            </button>
          ) : null}
          <button
            onClick={() => setAmountOpen(false)}
            className="flex-1 rounded-field bg-primary py-2.5 text-sm font-medium text-white transition hover:bg-primary-light"
          >
            Done
          </button>
        </div>
      </Sheet>
    </div>
  );
}
